import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useClienteUser } from "@/hooks/useClienteUser";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LogOut,
  Ship,
  Package,
  MapPin,
  Calendar,
  ChevronRight,
  Search,
  ArrowUpDown,
} from "lucide-react";
import { PortalDossierModal } from "@/components/portal/PortalDossierModal";
import { cn } from "@/lib/utils";

const ESTADO_PROGRESO: Record<string, number> = {
  "En Origen": 15,
  "En Tránsito": 45,
  "En Aduana": 75,
  "Nacionalizado": 95,
  "Entregado": 100,
};

const ESTADO_COLOR: Record<string, string> = {
  "En Origen": "bg-slate-100 text-slate-700 border-slate-200",
  "En Tránsito": "bg-blue-50 text-blue-700 border-blue-200",
  "En Aduana": "bg-amber-50 text-amber-700 border-amber-200",
  "Nacionalizado": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Entregado": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

type FilterPill = "todos" | "transito" | "aduana" | "finalizados";
type SortOption = "recientes" | "antiguos" | "eta" | "estado";

const PILLS: { id: FilterPill; label: string; estados: string[] }[] = [
  { id: "todos", label: "Todos", estados: [] },
  { id: "transito", label: "En Tránsito", estados: ["En Origen", "En Tránsito"] },
  { id: "aduana", label: "En Aduana", estados: ["En Aduana", "En Puerto"] },
  { id: "finalizados", label: "Finalizados", estados: ["Nacionalizado", "Entregado"] },
];

export default function PortalCliente() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [selectedEmbarqueId, setSelectedEmbarqueId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [activePill, setActivePill] = useState<FilterPill>("todos");
  const [sortBy, setSortBy] = useState<SortOption>("recientes");

  const { data: vinculos, isLoading: vinculosLoading } = useClienteUser();

  const clienteIds = (vinculos ?? []).map((v) => v.cliente_id);
  const cliente = vinculos?.[0];

  const { data: embarques, isLoading: embLoading } = useQuery({
    queryKey: ["portal-embarques", clienteIds],
    enabled: clienteIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("embarques")
        .select(
          "id, numero_do, estado_logistico, fecha_llegada_estimada, tracking_number, shipping_line, proveedor, created_at, registro_documentos(no_documento)",
        )
        .in("cliente_id", clienteIds)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Filtrado y ordenamiento en memoria
  const filteredEmbarques = useMemo(() => {
    if (!embarques) return [];
    const q = search.trim().toLowerCase();
    const pillEstados = PILLS.find((p) => p.id === activePill)?.estados ?? [];

    let result = embarques.filter((e: any) => {
      // Filtro por pill
      if (pillEstados.length > 0 && !pillEstados.includes(e.estado_logistico)) {
        return false;
      }
      // Filtro por búsqueda
      if (q) {
        const haystack = [
          e.numero_do,
          e.tracking_number,
          e.shipping_line,
          e.proveedor,
          ...(e.registro_documentos ?? []).map((d: any) => d.no_documento),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });

    // Ordenamiento
    result = [...result].sort((a: any, b: any) => {
      switch (sortBy) {
        case "antiguos":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case "eta": {
          const ea = a.fecha_llegada_estimada ? new Date(a.fecha_llegada_estimada).getTime() : Infinity;
          const eb = b.fecha_llegada_estimada ? new Date(b.fecha_llegada_estimada).getTime() : Infinity;
          return ea - eb;
        }
        case "estado":
          return (a.estado_logistico ?? "").localeCompare(b.estado_logistico ?? "");
        case "recientes":
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    return result;
  }, [embarques, search, activePill, sortBy]);

  // Conteos por pill (sobre el set base, antes de search)
  const pillCounts = useMemo(() => {
    const counts: Record<FilterPill, number> = { todos: 0, transito: 0, aduana: 0, finalizados: 0 };
    if (!embarques) return counts;
    counts.todos = embarques.length;
    for (const e of embarques as any[]) {
      for (const p of PILLS) {
        if (p.id === "todos") continue;
        if (p.estados.includes(e.estado_logistico)) counts[p.id]++;
      }
    }
    return counts;
  }, [embarques]);

  // Loading inicial
  if (authLoading || vinculosLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Cargando portal…</p>
      </div>
    );
  }

  // Sin sesión
  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  // Usuario sin vínculos como cliente
  if (!vinculos || vinculos.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md text-center bg-white rounded-2xl shadow-sm border p-10">
          <div className="h-12 w-12 rounded-full bg-amber-50 mx-auto flex items-center justify-center mb-4">
            <Ship className="h-5 w-5 text-amber-600" />
          </div>
          <h2 className="text-lg font-medium text-slate-900 mb-2">Acceso restringido</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Tu cuenta no está vinculada a ningún cliente del portal. Contacta a tu agencia
            para solicitar acceso.
          </p>
          <Button variant="outline" onClick={logout} className="gap-2">
            <LogOut className="h-4 w-4" />
            Cerrar sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* HEADER */}
      <header className="bg-white border-b sticky top-0 z-10 backdrop-blur-md bg-white/95">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center">
              <Ship className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                {cliente?.org_nombre}
              </p>
              <p className="text-sm font-medium text-slate-900 leading-tight">Portal Cliente</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Cliente
              </p>
              <p className="text-sm font-medium text-slate-900 leading-tight">
                {cliente?.cliente_nombre}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="gap-2">
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      {/* BODY */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-light tracking-tight text-slate-900 mb-1">
            Mis Embarques
          </h1>
          <p className="text-sm text-muted-foreground">
            Visibilidad en tiempo real del estado y costos de tus operaciones de importación.
          </p>
        </div>

        {/* TOOLBAR: Search + Sort */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por referencia, factura, contenedor o naviera…"
              className="pl-9 bg-white border-slate-200 h-11 rounded-xl"
            />
          </div>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
            <SelectTrigger className="w-full sm:w-[200px] h-11 rounded-xl bg-white border-slate-200 gap-2">
              <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recientes">Más recientes</SelectItem>
              <SelectItem value="antiguos">Más antiguos</SelectItem>
              <SelectItem value="eta">Próxima llegada (ETA)</SelectItem>
              <SelectItem value="estado">Estado (A–Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* PILLS */}
        <div className="flex flex-wrap gap-2 mb-6">
          {PILLS.map((p) => {
            const isActive = activePill === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setActivePill(p.id)}
                className={cn(
                  "inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium border transition-all",
                  isActive
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900",
                )}
              >
                <span>{p.label}</span>
                <span
                  className={cn(
                    "tabular-nums text-[10px] px-1.5 py-0.5 rounded-full",
                    isActive ? "bg-white/15 text-white" : "bg-slate-100 text-slate-500",
                  )}
                >
                  {pillCounts[p.id]}
                </span>
              </button>
            );
          })}
        </div>

        {embLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full rounded-2xl" />
            ))}
          </div>
        ) : filteredEmbarques.length === 0 ? (
          <div className="bg-white rounded-2xl border p-12 text-center">
            <Package className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-medium text-slate-900 mb-1">
              {search || activePill !== "todos"
                ? "Sin resultados"
                : "No hay embarques disponibles"}
            </p>
            <p className="text-sm text-muted-foreground">
              {search || activePill !== "todos"
                ? "Ajusta la búsqueda o el filtro para ver más embarques."
                : "Cuando tu agencia registre un embarque a tu nombre aparecerá aquí."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEmbarques.map((e: any) => {
              const progreso = ESTADO_PROGRESO[e.estado_logistico] ?? 10;
              const colorClass = ESTADO_COLOR[e.estado_logistico] ?? "bg-slate-100 text-slate-700";
              return (
                <button
                  key={e.id}
                  onClick={() => setSelectedEmbarqueId(e.id)}
                  className="w-full text-left bg-white rounded-2xl border p-6 hover:border-slate-300 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                          Embarque
                        </p>
                        <Badge
                          variant="outline"
                          className={`${colorClass} font-medium px-2.5 py-0.5 rounded-full text-xs`}
                        >
                          {e.estado_logistico}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 truncate">
                        {e.numero_do}
                      </h3>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs mb-4">
                    {e.shipping_line && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Ship className="h-3.5 w-3.5" />
                        <span className="truncate">{e.shipping_line}</span>
                      </div>
                    )}
                    {e.tracking_number && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="truncate font-mono">{e.tracking_number}</span>
                      </div>
                    )}
                    {e.fecha_llegada_estimada && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>ETA: {e.fecha_llegada_estimada}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                        Progreso
                      </span>
                      <span className="text-[11px] text-slate-600 font-medium tabular-nums">
                        {progreso}%
                      </span>
                    </div>
                    <Progress value={progreso} className="h-1.5" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>

      <PortalDossierModal
        embarqueId={selectedEmbarqueId}
        onClose={() => setSelectedEmbarqueId(null)}
      />
    </div>
  );
}
